// Quiz definitions: Love Language & Attachment Style

export const QUIZZES = {
  'love-language': {
    id: 'love-language',
    showSecondary: true,
    title: 'Love Language Quiz',
    emoji: '💌',
    tagline: 'Discover your top 2 love languages',
    intro: "7 quick questions. No signup. Find out your top 2 love languages, the ways love speaks loudest to your heart, and what they mean for your relationship.",
    seo: {
      title: 'Free Love Language Quiz | What\'s Your Love Language?',
      description: 'Take our free 2-minute Love Language quiz. Discover whether Words of Affirmation, Quality Time, Gifts, Acts of Service, or Physical Touch is your love language.',
      keywords: 'love language quiz, free love language test, 5 love languages, relationship quiz, couples quiz',
    },
    color: 'from-pink-500 to-rose-500',
    partnerNote: {
      title: 'Your result is only HALF the picture',
      text: 'Love languages work in pairs. If you speak Quality Time but your partner speaks Acts of Service, you can both be "saying I love you" every day, and neither of you hears it. Knowing your own language while your partner stays in the dark is like whispering in a locked room. If they don\'t take this test too, this result is basically useless.',
      cta: 'Send your partner this quiz right now',
    },
    appPitch: 'Mismatched love languages are behind half of all "you don\'t care about me" fights, and those escalate fast. That\'s exactly why we built **CoupleIn**: its Guided Mode walks you both through a fair-turn conversation that **solves heated arguments in 5 minutes or less.**',
    questions: [
      {
        q: 'After a long, exhausting week, what would make you feel most loved?',
        options: [
          { text: '"I\'m so proud of you, you handled this week like a champ."', key: 'words' },
          { text: 'A full evening together, phones off, just the two of us', key: 'time' },
          { text: 'Coming home to a small surprise they picked up for me', key: 'gifts' },
          { text: 'They quietly handled the chores so I could rest', key: 'acts' },
          { text: 'A long, wordless hug the moment I walk in the door', key: 'touch' },
        ],
      },
      {
        q: 'What would sting the most in a relationship?',
        options: [
          { text: 'Never hearing "I love you" or a compliment', key: 'words' },
          { text: 'Always feeling like they\'re too busy for me', key: 'time' },
          { text: 'My birthday passing with zero thought or effort', key: 'gifts' },
          { text: 'Asking for help and being ignored', key: 'acts' },
          { text: 'Feeling physically distant, no hugs, no hand-holding', key: 'touch' },
        ],
      },
      {
        q: 'Your idea of a perfect date night?',
        options: [
          { text: 'Deep conversation where they tell me what I mean to them', key: 'words' },
          { text: 'A whole day exploring somewhere new, side by side', key: 'time' },
          { text: 'They surprise me with tickets to something I mentioned once', key: 'gifts' },
          { text: 'They planned everything, I didn\'t have to lift a finger', key: 'acts' },
          { text: 'Slow dancing in the kitchen, close and cozy', key: 'touch' },
        ],
      },
      {
        q: 'Which small gesture instantly melts your heart?',
        options: [
          { text: 'A sweet "thinking of you" text in the middle of the day', key: 'words' },
          { text: 'They cancel plans just to spend time with me', key: 'time' },
          { text: 'They bring me my favorite snack "just because"', key: 'gifts' },
          { text: 'My coffee is ready before I even ask', key: 'acts' },
          { text: 'They reach for my hand while walking', key: 'touch' },
        ],
      },
      {
        q: 'How do YOU naturally show love?',
        options: [
          { text: 'I tell them, compliments, encouragement, love notes', key: 'words' },
          { text: 'I make time, I show up, fully present', key: 'time' },
          { text: 'I give thoughtful little gifts and surprises', key: 'gifts' },
          { text: 'I do things, errands, fixes, favors', key: 'acts' },
          { text: 'I\'m affectionate, hugs, kisses, cuddles', key: 'touch' },
        ],
      },
      {
        q: 'On your birthday, what matters most?',
        options: [
          { text: 'A heartfelt card or message that makes me tear up', key: 'words' },
          { text: 'Spending the entire day together', key: 'time' },
          { text: 'A gift that proves they really know me', key: 'gifts' },
          { text: 'They took care of everything, cake, plans, all of it', key: 'acts' },
          { text: 'Waking up to kisses and morning cuddles', key: 'touch' },
        ],
      },
      {
        q: 'During a rough patch, what would reassure you most?',
        options: [
          { text: '"We\'re going to be okay. I love you." Said out loud.', key: 'words' },
          { text: 'Them clearing their schedule to work through it with me', key: 'time' },
          { text: 'A small peace offering that shows they care', key: 'gifts' },
          { text: 'Them stepping up and easing my load without being asked', key: 'acts' },
          { text: 'Being held until the tension melts away', key: 'touch' },
        ],
      },
    ],
    results: {
      words: {
        title: 'Words of Affirmation',
        emoji: '💬',
        headline: 'Your heart runs on words.',
        description: 'Compliments, encouragement, and a genuine "I love you" light you up more than anything money can buy. When words are missing, you feel it deeply. Silence can hurt more than an argument.',
        tips: [
          'Tell your partner exactly which phrases mean the most to you',
          'Save sweet texts and re-read them on hard days',
          'Remember: your partner may show love differently, watch for their actions too',
        ],
        cta: 'CoupleIn\'s daily check-ins and Brownie Points give your partner easy, playful ways to say the words you need to hear, every single day.',
      },
      time: {
        title: 'Quality Time',
        emoji: '⏳',
        headline: 'Presence is your love currency.',
        description: 'You don\'t need grand gestures, you need undivided attention. Phones down, eyes up, fully there. Distraction and busyness feel like rejection, while a slow shared evening feels like everything.',
        tips: [
          'Protect a weekly "us time" ritual that nothing can bump',
          'Ask for full attention gently, "Can we do 20 phone-free minutes?"',
          'Quality beats quantity: 30 present minutes > 3 distracted hours',
        ],
        cta: 'CoupleIn\'s shared calendar makes sure date nights actually happen, and its 60-second check-in keeps you connected even on the busiest days.',
      },
      gifts: {
        title: 'Receiving Gifts',
        emoji: '🎁',
        headline: 'Thoughtfulness is your language.',
        description: 'It\'s never about the price tag, it\'s the proof that someone was thinking of you when you weren\'t around. A tiny "saw this and thought of you" gift can carry you for weeks.',
        tips: [
          'Let your partner know this isn\'t materialism, it\'s about being known',
          'Keep a shared wishlist so gifting is easy for both of you',
          'Celebrate the small surprises as loudly as the big ones',
        ],
        cta: 'CoupleIn\'s Brownie Points system turns thoughtful little surprises into a game you both win, and reminders make sure no special date slips by.',
      },
      acts: {
        title: 'Acts of Service',
        emoji: '🤝',
        headline: 'For you, love is a verb.',
        description: '"Let me handle that for you" is your ultimate romance. Actions speak infinitely louder than words, when your partner lightens your load, you feel deeply cherished and safe.',
        tips: [
          'Ask specifically, partners aren\'t mind readers about which tasks matter',
          'Notice and appreciate the invisible things they already do',
          'Trade tasks playfully instead of keeping score silently',
        ],
        cta: 'CoupleIn\'s Brownie Points turn everyday favors into playful appreciation, so the helpful things you both do never go unnoticed again.',
      },
      touch: {
        title: 'Physical Touch',
        emoji: '🫂',
        headline: 'Closeness is how you connect.',
        description: 'A hug, a hand squeeze, sitting shoulder-to-shoulder, physical closeness grounds you and tells you everything is okay. Distance, even in the same room, feels like a wall.',
        tips: [
          'Build small touch rituals, a 10-second hug hello and goodbye',
          'During conflict, a gentle hand on the arm can defuse everything',
          'Communicate that touch is connection, not always an invitation',
        ],
        cta: 'CoupleIn\'s mood sync helps you sense when your partner needs closeness, so you can reach out at exactly the right moment.',
      },
    },
  },

  'attachment-style': {
    id: 'attachment-style',
    title: 'Attachment Style Quiz',
    emoji: '🔗',
    tagline: 'Understand how you bond in relationships',
    intro: "7 quick questions. No signup. Discover whether you're Secure, Anxious, Avoidant, or Fearful-Avoidant, and what it means for how you love.",
    seo: {
      title: 'Free Attachment Style Quiz | Secure, Anxious or Avoidant?',
      description: 'Take our free 2-minute Attachment Style quiz. Find out if you\'re Secure, Anxious, Avoidant, or Fearful-Avoidant and learn how it shapes your relationship.',
      keywords: 'attachment style quiz, free attachment test, anxious attachment, avoidant attachment, secure attachment, relationship quiz',
    },
    color: 'from-purple-500 to-pink-500',
    questions: [
      {
        q: 'Your partner hasn\'t replied to your text for hours. What\'s your honest first reaction?',
        options: [
          { text: 'They\'re probably busy, I\'ll hear back later', key: 'secure' },
          { text: 'I re-read my message and start worrying I upset them', key: 'anxious' },
          { text: 'Honestly? I barely notice. I like the breathing room', key: 'avoidant' },
          { text: 'I want to reach out but stop myself, I don\'t want to seem needy', key: 'fearful' },
        ],
      },
      {
        q: 'When a conflict starts brewing, you tend to…',
        options: [
          { text: 'Address it calmly, disagreements are normal', key: 'secure' },
          { text: 'Need to resolve it RIGHT NOW, unresolved tension eats at me', key: 'anxious' },
          { text: 'Shut down or walk away, I need space to think', key: 'avoidant' },
          { text: 'Swing between wanting to fix it and wanting to disappear', key: 'fearful' },
        ],
      },
      {
        q: 'How do you feel about depending on your partner?',
        options: [
          { text: 'Comfortable, we lean on each other and it works', key: 'secure' },
          { text: 'I depend on them a lot, maybe more than they depend on me', key: 'anxious' },
          { text: 'I prefer handling things myself, depending on people backfires', key: 'avoidant' },
          { text: 'I crave it, but trusting someone that much scares me', key: 'fearful' },
        ],
      },
      {
        q: 'Your partner asks for more closeness and intimacy. You feel…',
        options: [
          { text: 'Happy, deeper connection sounds great', key: 'secure' },
          { text: 'Relieved. Finally, they want what I\'ve wanted all along', key: 'anxious' },
          { text: 'A little suffocated, I love them, but I need my space', key: 'avoidant' },
          { text: 'Torn, I want closeness but panic when it actually arrives', key: 'fearful' },
        ],
      },
      {
        q: 'After a big argument, what happens inside you?',
        options: [
          { text: 'It stings, but I trust we\'ll repair it', key: 'secure' },
          { text: 'I spiral, "are they going to leave me over this?"', key: 'anxious' },
          { text: 'I go numb and act like nothing happened', key: 'avoidant' },
          { text: 'I feel flooded, angry, scared, and ashamed all at once', key: 'fearful' },
        ],
      },
      {
        q: 'Opening up about your deepest feelings feels…',
        options: [
          { text: 'Natural with the right person', key: 'secure' },
          { text: 'Necessary, I share a lot and need reassurance back', key: 'anxious' },
          { text: 'Uncomfortable, I keep the heavy stuff to myself', key: 'avoidant' },
          { text: 'Terrifying, I\'ve been hurt when I\'ve been vulnerable before', key: 'fearful' },
        ],
      },
      {
        q: 'Deep down, what do you believe about love?',
        options: [
          { text: 'Love is safe when two people keep choosing each other', key: 'secure' },
          { text: 'Love is amazing but I always fear losing it', key: 'anxious' },
          { text: 'Love is nice, but relying on it too much is risky', key: 'avoidant' },
          { text: 'Love is what I want most and what scares me most', key: 'fearful' },
        ],
      },
    ],
    partnerNote: {
      title: 'Your result is only HALF the picture',
      text: 'Attachment styles don\'t exist in a vacuum. They collide. An anxious partner paired with an avoidant one creates the classic pursue-withdraw loop: one chases, the other retreats, and both end up hurt. Knowing YOUR style while your partner stays in the dark is like learning half a dance. If they don\'t take this test too, this result is basically useless.',
      cta: 'Send your partner this quiz right now',
    },
    appPitch: 'When two different attachment styles clash, arguments get heated fast, the anxious one pushes, the avoidant one shuts down, and nothing gets resolved. That\'s exactly why we built **CoupleIn**: its Guided Mode walks you both through a fair-turn conversation that **solves heated arguments in 5 minutes or less.**',
    results: {
      secure: {
        title: 'Secure Attachment',
        emoji: '🌳',
        headline: 'You love from solid ground.',
        description: 'You\'re comfortable with both intimacy and independence. Conflict doesn\'t shake your foundation. You trust that repair is possible. Partners feel safe with you, and you know how to ask for what you need.',
        deepDive: [
          {
            heading: 'Where it comes from',
            text: 'Secure attachment usually forms when caregivers were consistently responsive, you learned early that reaching out gets a warm answer. As an adult, you carry a quiet inner belief: "I am worthy of love, and people can be trusted." That belief is your relationship superpower.',
          },
          {
            heading: 'How it shows up in your relationship',
            text: 'You can say "that hurt me" without staging a crisis, and you can hear "I need space" without hearing "I don\'t love you." You give your partner room to be human. During conflict you stay in the ring, you don\'t flee and you don\'t cling. Repair comes naturally to you, which makes your relationships feel remarkably stable to others.',
          },
          {
            heading: 'Your blind spot',
            text: 'Security can breed complacency. Because you don\'t feel relationship anxiety, you may miss the signals of a partner who does, the double texts, the withdrawal, the testing. Around 40-45% of adults are NOT securely attached, so odds are decent your partner processes love and conflict very differently than you do. Your calm can accidentally read as indifference to an anxious partner, or as pressure to an avoidant one.',
          },
        ],
        tips: [
          'Your steadiness is a gift, model calm repair after conflicts',
          'Stay curious about your partner\'s attachment style; theirs may differ',
          'Keep investing in rituals of connection, security is maintained, not owned',
        ],
        cta: 'Even secure couples drop balls. CoupleIn keeps your strong foundation strong with shared calendars, daily check-ins, and playful appreciation.',
      },
      anxious: {
        title: 'Anxious Attachment',
        emoji: '🌊',
        headline: 'You love deeply, and fear losing it.',
        description: 'You\'re incredibly attuned to your partner and give love generously. But uncertainty is your kryptonite: unanswered texts and unclear signals can send your mind racing. What you crave most is consistent reassurance.',
        deepDive: [
          {
            heading: 'Where it comes from',
            text: 'Anxious attachment typically develops when caregiving was inconsistent: sometimes warm, sometimes absent. You learned that love exists but can vanish, so your nervous system became an early-warning radar, constantly scanning for signs of distance. It\'s not neediness. It\'s a survival strategy you learned before you could talk.',
          },
          {
            heading: 'How it shows up in your relationship',
            text: 'You notice everything: the shorter text, the delayed reply, the slightly-off tone. When you sense distance, your attachment system "hyperactivates", you pursue, over-text, re-read conversations, seek reassurance, or use protest behaviors (going cold to see if they chase). During arguments, you NEED resolution right now; an unresolved fight at bedtime feels physically unbearable.',
          },
          {
            heading: 'Your blind spot',
            text: 'Your pursuit of closeness can accidentally push it away, especially with an avoidant partner, where your pressing triggers their retreating, which triggers more pressing. This pursue-withdraw loop is the single most common destructive pattern in couples. The way out isn\'t trying harder; it\'s structured, predictable connection that keeps your radar calm before it fires.',
          },
        ],
        tips: [
          'Name the need: "A quick goodnight text really settles me"',
          'Practice self-soothing before seeking reassurance, then ask calmly',
          'Notice the evidence of love that\'s already there, daily',
        ],
        cta: 'CoupleIn\'s daily mood sync and check-ins give you the consistent connection your heart craves. No more guessing where you stand.',
      },
      avoidant: {
        title: 'Avoidant Attachment',
        emoji: '🏔️',
        headline: 'You love with a need for space.',
        description: 'You\'re independent, self-sufficient, and calm under pressure. But when closeness intensifies, part of you pulls back. It\'s not that you don\'t love, it\'s that vulnerability feels risky, so you protect yourself with distance.',
        deepDive: [
          {
            heading: 'Where it comes from',
            text: 'Avoidant attachment usually develops when emotional needs were met with dismissal or discomfort, you learned that expressing feelings didn\'t help, so you stopped. Self-reliance became your armor. As an adult, "I don\'t need anyone" isn\'t arrogance; it\'s the shape your protection took.',
          },
          {
            heading: 'How it shows up in your relationship',
            text: 'You love deeply but express it through actions more than words. When conflict heats up or emotions run high, your system "deactivates", you go quiet, change the subject, suddenly need to be somewhere else, or mentally check out mid-argument. Partners often experience this as a wall. Inside, you\'re usually not cold at all, you\'re overwhelmed and protecting yourself the only way you learned how.',
          },
          {
            heading: 'Your blind spot',
            text: 'Your retreat feels like self-preservation to you, but it reads as abandonment to your partner, especially an anxious one, whose pursuit will intensify the more you withdraw. The pursue-withdraw loop punishes you both. What actually works for avoidants isn\'t forced vulnerability marathons, it\'s short, structured, time-boxed connection where you know exactly when it ends.',
          },
        ],
        tips: [
          'Tell your partner space isn\'t rejection: "I need an hour, then I\'m all yours"',
          'Practice micro-vulnerability, share one real feeling a day',
          'Notice when "I\'m fine" is actually a wall',
        ],
        cta: 'CoupleIn\'s 60-second check-in is connection without the overwhelm, stay close on your terms, one small moment at a time.',
      },
      fearful: {
        title: 'Fearful-Avoidant Attachment',
        emoji: '🌗',
        headline: 'You crave love and brace for hurt.',
        description: 'You feel the pull of deep connection and the push of self-protection, often at the same time. Relationships can feel like a beautiful storm. The good news: this style softens dramatically with safety, structure, and small consistent wins.',
        deepDive: [
          {
            heading: 'Where it comes from',
            text: 'Fearful-avoidant (disorganized) attachment often develops when the source of comfort was also a source of fear or chaos. You learned two contradictory lessons at once: "I need closeness to survive" and "closeness is dangerous." That\'s why intimacy can feel like standing in a doorway you can\'t walk through or walk away from.',
          },
          {
            heading: 'How it shows up in your relationship',
            text: 'You run hot and cold, intensely close one week, distant the next, and it confuses your partner as much as it confuses you. During arguments you can get emotionally flooded: anger, fear, and shame arrive all at once, and you might lash out and then withdraw in the same hour. Afterward, the self-criticism is brutal. None of this means you\'re broken; it means your system learned to expect love and danger from the same place.',
          },
          {
            heading: 'Your blind spot',
            text: 'Your push-pull can exhaust even a secure partner if neither of you can name what\'s happening. The research is genuinely hopeful though: fearful-avoidant attachment responds better to structure than any other style. Predictable rituals, low-stakes daily connection, and time-limited conflict conversations retrain your nervous system that closeness is safe. Intensity is not the answer, consistency is.',
          },
        ],
        tips: [
          'Go slow, consistency over intensity builds your trust',
          'When you feel the urge to run, name it out loud instead',
          'Celebrate small safe moments; they rewire the fear over time',
        ],
        cta: 'CoupleIn creates the steady, low-pressure structure you need, gentle daily check-ins and guided conversations that make closeness feel safe.',
      },
    },
  },
};
